import React from "react";
import { motion } from "framer-motion";
import { Quote, Star, Sparkles } from "lucide-react";
import PageMeta from "../site/PageMeta";
import Chapter from "../site/Chapter";
import { testimonials } from "../home/HomeTestimonials";

const cases = [
  {
    tag: "Account Executive",
    industry: "Healthcare",
    accent: "#00E5FF",
    challenge:
      "A revenue-critical role with a 5-stage interview process — the kind that normally drags past a month.",
    result:
      "Qualified shortlist in under a week · Placed in under 25 days · Cleared all 5 interview stages",
    resultChips: [
      "Shortlist < 1 week",
      "Placed < 25 days",
      "5 stages cleared",
    ],
    standout:
      "The hire came from a company in the exact same business as the client — no ramp-up time needed.",
  },
  {
    tag: "Sales Role",
    industry: "Germany · B2B",
    accent: "#A3FF5E",
    challenge:
      "Sourcing in Germany, with a language barrier most search partners can't navigate.",
    result: "297 sourced → 217 shortlisted → 146 contacted → 20 advanced to final screening",
    funnel: [
      { n: "297", label: "Sourced" },
      { n: "217", label: "Shortlisted" },
      { n: "146", label: "Contacted" },
      { n: "20", label: "Final screening" },
    ],
    standout:
      "A 73% shortlist rate despite the language barrier — usually the first thing that tanks candidate quality.",
  },
  {
    tag: "Managing Director",
    industry: "France · Retail",
    accent: "#007AFF",
    challenge:
      "A high-level executive search in a small, specialized talent pool.",
    result: "92 sourced → 57 shortlisted → 49 contacted → 13 advanced to final screening",
    funnel: [
      { n: "92", label: "Sourced" },
      { n: "57", label: "Shortlisted" },
      { n: "49", label: "Contacted" },
      { n: "13", label: "Final screening" },
    ],
    standout:
      "Executive-level quality held up even in a narrow market.",
  },
];

export default function CaseStudies() {
  return (
    <>
      <PageMeta
        title="Case Studies & Testimonials — A4 Assist"
        description="Real placements, real numbers. Featured search outcomes across healthcare, B2B sales in Germany, and executive retail search in France."
      />

      <section className="pt-36 pb-14">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <Chapter
            number="00"
            eyebrow="Case Studies & Testimonials"
            title={
              <>
                What it's like to{" "}
                <span className="font-serif italic">work</span> with us.
              </>
            }
          />
        </div>
      </section>

      {/* Long-form testimonials */}
      <section className="pb-20">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <motion.figure
              key={t.name}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className="relative rounded-2xl border border-slate-800 bg-[#0A1325]/60 overflow-hidden flex flex-col"
              data-testid={`case-testimonial-${i}`}
            >
              <div className="portrait-spotlight h-56">
                <img
                  src={t.portrait}
                  alt={t.name}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-7 flex-1 flex flex-col">
                <div className="flex items-center gap-1 text-[#A3FF5E]">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={13} fill="currentColor" stroke="none" />
                  ))}
                </div>
                <Quote size={26} className="text-[#00E5FF]/40 mt-3" />
                <blockquote className="pull-quote mt-3 text-lg leading-snug text-white/95 flex-1">
                  “{t.quote}”
                </blockquote>
                <div className="mt-6 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20 self-start font-semibold">
                  {t.highlight}
                </div>
                <figcaption className="mt-6 pt-4 border-t border-slate-800 flex items-center justify-between">
                  <div>
                    <div className="font-display text-sm font-semibold text-white">
                      {t.name}
                    </div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </figcaption>
              </div>
            </motion.figure>
          ))}
        </div>
      </section>

      {/* Featured cases — 3 stat-forward cards */}
      <section
        id="featured-cases"
        data-testid="featured-cases-section"
        className="pb-32 pt-16 border-t border-slate-900"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <Chapter
            number="01"
            eyebrow="Featured Cases"
            title={
              <>
                Real placements.{" "}
                <span className="font-serif italic">Real</span> numbers.
              </>
            }
          />

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-5">
            {cases.map((c, i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                data-testid={`case-card-${i}`}
                className="group relative rounded-2xl border border-slate-800 bg-gradient-to-b from-[#0A1325] to-[#070F1F] overflow-hidden flex flex-col"
              >
                <div
                  className="absolute -top-24 -right-24 h-56 w-56 rounded-full opacity-[0.12] blur-3xl"
                  style={{ background: c.accent }}
                />

                {/* Header */}
                <div className="relative p-7 pb-5 border-b border-slate-800/60">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div
                        className="font-display text-[10px] font-bold tracking-[0.24em] uppercase"
                        style={{ color: c.accent }}
                      >
                        {c.industry}
                      </div>
                      <h3 className="font-display mt-2 text-xl sm:text-2xl font-semibold text-white leading-tight">
                        {c.tag}
                      </h3>
                    </div>
                    <span className="font-display text-xs font-semibold text-slate-600 tabular-nums">
                      0{i + 1}
                    </span>
                  </div>
                </div>

                {/* Challenge */}
                <div className="relative p-7 pb-5">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500">
                    Challenge
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">
                    {c.challenge}
                  </p>
                </div>

                {/* Result — funnel or chips */}
                <div className="relative px-7 pb-6">
                  <div
                    className="text-[10px] tracking-[0.22em] uppercase font-bold"
                    style={{ color: c.accent }}
                  >
                    Result
                  </div>

                  {c.funnel ? (
                    <div
                      className="mt-3 grid grid-cols-4 gap-2"
                      data-testid={`case-funnel-${i}`}
                    >
                      {c.funnel.map((f, j) => (
                        <div
                          key={j}
                          className="rounded-lg border border-slate-800 bg-[#050B14]/70 px-2.5 py-3"
                        >
                          <div className="font-display text-xl font-bold text-white tabular-nums leading-none">
                            {f.n}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-1 leading-tight">
                            {f.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {c.resultChips.map((r, j) => (
                        <span
                          key={j}
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border"
                          style={{
                            color: c.accent,
                            background: `${c.accent}12`,
                            borderColor: `${c.accent}40`,
                          }}
                        >
                          {r}
                        </span>
                      ))}
                    </div>
                  )}

                  <p className="mt-3 text-[11px] text-slate-500 tabular-nums leading-relaxed">
                    {c.result}
                  </p>
                </div>

                {/* Standout — italic serif pull */}
                <div className="relative mt-auto px-7 py-6 border-t border-slate-800/60 bg-[#050B14]/40">
                  <div className="flex items-start gap-3">
                    <Sparkles
                      size={16}
                      className="mt-1 shrink-0"
                      style={{ color: c.accent }}
                    />
                    <p className="pull-quote text-base leading-snug text-white/95">
                      {c.standout}
                    </p>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
